// The browser client, not the server one: Oak imports modules out of this
// directory directly, so a server-client import here would break its build. Both
// paths expose the same UnitType and DensityUnits values.
import { DensityUnits, UnitType } from "encamp-prisma/generated/browser";

import { getParsedAmountCodeOrNull } from "../constants/storageAmounts";
import { hasValue } from "../utils/hasValue";
import { prettyPrintEnumValue } from "../utils/prettyPrintEnumValue";
import { AmountField, applyStatePrecision, FLOATING_POINT_PRECISION } from "./amountPrecision";
import { AggregationError, ReportingUnitOverrideNotApplied } from "./errors";
import { convert } from "./unitConversions";

/**
 * A staff reporting-unit override, as much of it as this conversion needs.
 * Reason and audit columns live on the row but do not affect the arithmetic.
 */
export type ReportingUnitOverride = {
  unit: UnitType;
};

/**
 * The per-storage-location fields this transform reads or writes. A
 * `ReportingFacilityChemicalStorageLocation` row carries many other fields
 * (storageLocationId, storageType, ...); this type only names the ones the
 * override touches, and every caller's richer row type flows through the
 * conversion untouched.
 */
export type OverridableStorageLocationFields = {
  unit?: UnitType | null;
  maxAmount?: number | null;
  averageAmount?: number | null;
  maxAmountLargestContainer?: number | null;
};

/** The RFC fields this transform reads or writes. */
export type OverridableRfcFields = {
  unit?: UnitType | null;
  maxAmount?: number | null;
  averageAmount?: number | null;
  maxAmountLargestContainer?: number | null;
  maxAmountCode?: number | null;
  averageAmountCode?: number | null;
  aggregationErrorMessage?: string | null;
  preOverrideUnit?: UnitType | null;
  preOverrideMaxAmount?: number | null;
  preOverrideAverageAmount?: number | null;
  /**
   * Per-storage-location child rows. They carry their own amounts and must be
   * restated in lockstep with the parent — converted by the identical
   * `convert` call when the parent's override applies, left untouched when it
   * is refused. See the module doc for why a partial application is worse than
   * no application at all.
   */
  storageLocations?: OverridableStorageLocationFields[] | null;
};

type OverrideAppliedFields = {
  unit: UnitType | null;
  maxAmount: number | null;
  averageAmount: number | null;
  maxAmountLargestContainer: number | null;
  maxAmountCode: number | null;
  averageAmountCode: number | null;
  aggregationErrorMessage: string | null;
  preOverrideUnit: UnitType | null;
  preOverrideMaxAmount: number | null;
  preOverrideAverageAmount: number | null;
};

export type RfcWithOverrideApplied<T extends OverridableRfcFields> = Omit<
  T,
  keyof OverrideAppliedFields
> &
  OverrideAppliedFields;

/**
 * Why an override that exists could not be honoured. Handed to the caller so it
 * can log; this module performs no I/O of its own.
 */
export type ReportingUnitOverrideRefusal = {
  targetUnit: UnitType;
  sourceUnit: UnitType | null;
  /** Set when the refusal came from one field's conversion failing. */
  field?: AmountField;
  /** The message describing the refusal. */
  message: string;
  /**
   * Whether `message` was written to the row's `aggregationErrorMessage`. False
   * when the row already carried an aggregation error, which is left in place
   * because it matters more — in that case the logs are the only trace, which is
   * exactly why this callback fires there.
   */
  recorded: boolean;
  error: AggregationError;
};

export type ApplyReportingUnitOverrideArgs<T extends OverridableRfcFields> = {
  rfc: T;
  /** Null or undefined means no override exists for this facility + chemical. */
  override: ReportingUnitOverride | null | undefined;
  /** The facility's state, which selects the range-code table. */
  state: string | null | undefined;
  density: number | null | undefined;
  densityUnits: DensityUnits | null | undefined;
  onRefusal?: (refusal: ReportingUnitOverrideRefusal) => void;
};

/**
 * Restates an aggregated RFC's reported figures in the unit staff pinned for
 * this facility + chemical.
 *
 * It changes how a quantity is *expressed*, never whether the chemical is
 * reportable: `isOverThreshold`, `thresholdReason` and `willReport` keep the
 * statutory basis threshold calculation gave them, on the refusal path too.
 * The `preOverride*` fields record what aggregation computed, which would
 * otherwise be unrecoverable once the reported figures are persisted.
 *
 * ## All or nothing
 *
 * Applied completely or not at all. Every partial state emits a figure in a
 * unit it was not measured in — relabelling without converting overstates by
 * the density factor, and a range code carries no unit of its own, so a stale
 * code reads as the converted amount's.
 *
 * It is unhonourable without a usable density for a mass/volume crossing, or
 * without an exact amount where a range code exists. The resolver refuses both
 * up front, so this path means the data changed under an override that was
 * valid when saved. On refusal the row is returned as aggregation computed it,
 * `preOverride*` stays null, and `aggregationErrorMessage` explains why.
 *
 * ## Field-by-field policy when the override is applied
 *
 * | Field                                | Policy                            |
 * | ------------------------------------ | --------------------------------- |
 * | unit                                 | override's unit                   |
 * | maxAmount / averageAmount            | converted                         |
 * | maxAmountLargestContainer            | converted                         |
 * | maxAmountCode / averageAmountCode    | recomputed from converted amount  |
 * | storageLocations[].unit              | override's unit                   |
 * | storageLocations[].maxAmount / ..    | converted, same call as the parent|
 * | dailyAmount, dailyMaxAmountLargest.. | untouched, still the source unit  |
 * | isOverThreshold, thresholdReason,    | untouched                         |
 * | willReport                           |                                   |
 *
 * The daily arrays stay in the source unit: INITIAL reports recompute amounts
 * from them at read time and re-apply this override afterwards, so converting
 * them here would apply the factor twice.
 *
 * Storage-location children carry their own unit and amounts and are restated
 * through the identical `convert` call as the parent, so a failure on any one
 * child refuses the parent too.
 */
export function applyReportingUnitOverride<T extends OverridableRfcFields>({
  rfc,
  override,
  state,
  density,
  densityUnits,
  onRefusal,
}: ApplyReportingUnitOverrideArgs<T>): RfcWithOverrideApplied<T> {
  // Lazy: almost no row carries an override, and this runs once per chemical
  // per facility on every aggregation run.
  const unchanged = (): RfcWithOverrideApplied<T> => ({
    ...rfc,
    unit: rfc.unit ?? null,
    maxAmount: rfc.maxAmount ?? null,
    averageAmount: rfc.averageAmount ?? null,
    maxAmountLargestContainer: rfc.maxAmountLargestContainer ?? null,
    maxAmountCode: rfc.maxAmountCode ?? null,
    averageAmountCode: rfc.averageAmountCode ?? null,
    aggregationErrorMessage: rfc.aggregationErrorMessage ?? null,
    preOverrideUnit: rfc.preOverrideUnit ?? null,
    preOverrideMaxAmount: rfc.preOverrideMaxAmount ?? null,
    preOverrideAverageAmount: rfc.preOverrideAverageAmount ?? null,
  });

  if (!override) {
    return unchanged();
  }

  /**
   * Builds the refusal and hands it to the caller. Returns the message so the
   * two refusal shapes — one that records the message, one that cannot — share
   * a single description of what went wrong.
   */
  const reportRefusal = (reason: string, recorded: boolean, field?: AmountField): string => {
    const error = new ReportingUnitOverrideNotApplied({
      targetUnit: prettyPrintEnumValue(override.unit),
      sourceUnit: prettyPrintEnumValue(rfc.unit),
      reason,
    });
    const message = error.aggregatorError ?? error.message;
    onRefusal?.({
      targetUnit: override.unit,
      sourceUnit: rfc.unit ?? null,
      field,
      message,
      recorded,
      error,
    });
    return message;
  };

  const refuse = (reason: string, field?: AmountField): RfcWithOverrideApplied<T> => ({
    ...unchanged(),
    aggregationErrorMessage: reportRefusal(reason, true, field),
  });

  // The row already carries an aggregation error, and that error is more
  // important to surface than this refusal, so it is left in place.
  //
  // Such a row is not necessarily an error skeleton. EHS cross-pollination sets
  // a message on rows that aggregated completely and hold real amounts
  // (ehsAggregationHelper.ts, "The reporting status of this chemical could be
  // inaccurate due to errors on ..."), and sulfuric acid is an EHS, so this is
  // on the path the feature was built for. The refusal is therefore reported to
  // the caller — the logs are its only trace on this path.
  if (hasValue(rfc.aggregationErrorMessage)) {
    reportRefusal(
      "This chemical already has an aggregation error, which is shown instead of this one.",
      false
    );
    return unchanged();
  }

  if (!state) {
    return refuse("The facility has no state, so a storage range code cannot be recomputed.");
  }

  // A range code with no exact amount beneath it cannot be carried into another
  // unit. Checked before any conversion, because the refusal is the same either
  // way and this reason is more specific than a conversion failure's.
  const codeOnlyAmount = findCodeWithoutAmount(rfc);
  if (codeOnlyAmount) {
    return refuse(
      `The ${codeOnlyAmount} amount is reported as a storage range code rather than an exact ` +
        `amount, and a range cannot be converted. Enter an exact amount to use this override.`
    );
  }

  const failures: { field: AmountField; error: unknown }[] = [];
  const convertAmount = (amount: number | null | undefined, field: AmountField): number | null => {
    if (!hasValue(amount)) {
      return null;
    }
    try {
      const converted = convert(amount, rfc.unit, override.unit, density, densityUnits);
      if (!hasValue(converted)) {
        return null;
      }
      // Normalize exactly as aggregation does for its own amounts, so the
      // persisted figure and the code derived from it agree, and so a
      // conversion cannot smuggle more precision into the column than the
      // state accepts.
      return applyStatePrecision(
        Number(converted.toPrecision(FLOATING_POINT_PRECISION)),
        field,
        state
      );
    } catch (error) {
      // `convert` throws MissingDensity for an unconvertible mass<->volume pair
      // and UnsupportedUnitConversion for pairs it cannot handle at all.
      failures.push({ field, error });
      return null;
    }
  };

  const maxAmount = convertAmount(rfc.maxAmount, "maxAmount");
  const averageAmount = convertAmount(rfc.averageAmount, "averageAmount");
  const maxAmountLargestContainer = convertAmount(
    rfc.maxAmountLargestContainer,
    "maxAmountLargestContainer"
  );

  // Storage-location children convert through the identical `convertAmount`
  // closure the parent uses — same source unit, same target unit, same
  // density — so a failure on any child lands in the same `failures` array
  // the parent's own fields feed. That is what makes the refusal below cover
  // parent and children as one unit: there is no separate child-only failure
  // path to fall out of sync with the parent's.
  const storageLocations = rfc.storageLocations
    ? rfc.storageLocations.map((sl) => ({
        ...sl,
        unit: override.unit,
        maxAmount: convertAmount(sl.maxAmount, "storageLocationMaxAmount"),
        averageAmount: convertAmount(sl.averageAmount, "averageAmount"),
        maxAmountLargestContainer: convertAmount(
          sl.maxAmountLargestContainer,
          "maxAmountLargestContainer"
        ),
      }))
    : rfc.storageLocations;

  // One failed field — the parent's or a child's — refuses the whole
  // override. Converting the rest would put two units on one row under a
  // single label, and a child left behind in the old unit while the parent
  // moves is exactly that mislabelling at the row-set level.
  const firstFailure = failures[0];
  if (firstFailure) {
    return refuse(describeConversionFailure(firstFailure.error), firstFailure.field);
  }

  return {
    ...rfc,
    unit: override.unit,
    maxAmount,
    averageAmount,
    maxAmountLargestContainer,
    storageLocations,
    maxAmountCode: hasValue(maxAmount) ? getParsedAmountCodeOrNull(maxAmount, state) : null,
    averageAmountCode: hasValue(averageAmount)
      ? getParsedAmountCodeOrNull(averageAmount, state)
      : null,
    aggregationErrorMessage: null,
    preOverrideUnit: rfc.unit ?? null,
    preOverrideMaxAmount: rfc.maxAmount ?? null,
    preOverrideAverageAmount: rfc.averageAmount ?? null,
  };
}

/**
 * The amount field a range code with no exact amount beneath it names — "max"
 * or "average", matching `findCodeWithoutAmount`'s two fields. Exported for
 * `canApplyReportingUnitOverride`, which names the same field in its own
 * refusal so the resolver and UI can say which amount is the problem instead
 * of a generic message.
 */
export type CodeOnlyAmountField = "max" | "average";

/** Names the amount that has a range code but no exact amount, if either does. */
export function findCodeWithoutAmount(rfc: OverridableRfcFields): CodeOnlyAmountField | undefined {
  if (hasValue(rfc.maxAmountCode) && !hasValue(rfc.maxAmount)) {
    return "max";
  }
  if (hasValue(rfc.averageAmountCode) && !hasValue(rfc.averageAmount)) {
    return "average";
  }
  return undefined;
}

/**
 * The parent-level amount fields a reporting-unit override converts in
 * lockstep — the same three `convertAmount` above is called with. Exported so
 * `canApplyReportingUnitOverride` can probe each field's *actual* value
 * through `convert`, rather than a stand-in amount, and so inherit this
 * module's own null/zero handling instead of re-deriving it.
 */
export function overridableAmountFields(rfc: OverridableRfcFields): (number | null | undefined)[] {
  return [rfc.maxAmount, rfc.averageAmount, rfc.maxAmountLargestContainer];
}

function describeConversionFailure(error: unknown): string {
  if (error instanceof AggregationError) {
    return error.message;
  }
  return "The amounts could not be converted into that unit.";
}
